import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

// Declared alongside the status mapping, so the dialog's choices and what they
// mean cannot drift apart again. Re-exported for existing importers.
import type { RemovalReason } from '@/lib/lifecycle/releaseDisposition';
export type { RemovalReason };

interface RemoveRecallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: RemovalReason, otherReason?: string) => void;
  recallTitle?: string;
  campaignNumber?: string;
  vin?: string;
}

interface ReasonOptionButtonProps {
  option: { value: RemovalReason; label: string };
  selectedReason: RemovalReason | '';
  onSelect: (value: RemovalReason) => void;
}

const ReasonOptionButton: React.FC<ReasonOptionButtonProps> = ({
  option,
  selectedReason,
  onSelect
}) => {
  const isSelected = selectedReason === option.value;
  return (
    <button
      key={option.value}
      type='button'
      onClick={() => onSelect(option.value)}
      className={cn(
        'w-full flex items-center space-x-3 p-3 rounded-lg border-2 text-left',
        'transition-colors',
        isSelected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'
      )}
    >
      <div
        className={cn(
          'flex h-5 w-5 items-center justify-center rounded-full border-2',
          'transition-colors',
          isSelected ? 'border-primary bg-primary' : 'border-muted-foreground'
        )}
      >
        {isSelected && <Check className='h-3 w-3 text-primary-foreground' />}
      </div>
      <Label className='cursor-pointer font-normal flex-1'>{option.label}</Label>
    </button>
  );
};

const REASON_OPTIONS: Array<{ value: RemovalReason; label: string }> = [
  { value: 'vehicle_not_there', label: 'Vehicle not available' },
  { value: 'completed', label: 'Completed' },
  { value: 'other', label: 'Other reason' }
];

interface DialogDescriptionContentProps {
  recallTitle?: string;
  campaignNumber?: string;
  vin?: string;
}

const DialogDescriptionContent: React.FC<DialogDescriptionContentProps> = ({
  recallTitle,
  campaignNumber,
  vin
}) => {
  if (!recallTitle && !vin) return null;

  return (
    <>
      {recallTitle && (
        <div className='mt-2 text-sm font-medium'>
          {campaignNumber && <span className='text-muted-foreground'>{campaignNumber}: </span>}
          {recallTitle}
        </div>
      )}
      {vin && <div className='mt-1 text-xs text-muted-foreground font-mono'>{vin}</div>}
    </>
  );
};

interface ReasonSelectionProps {
  selectedReason: RemovalReason | '';
  onSelect: (reason: RemovalReason) => void;
  otherReasonText: string;
  onOtherReasonChange: (text: string) => void;
}

const ReasonSelection: React.FC<ReasonSelectionProps> = ({
  selectedReason,
  onSelect,
  otherReasonText,
  onOtherReasonChange
}) => {
  return (
    <div className='space-y-4 py-4'>
      <div className='space-y-2'>
        {REASON_OPTIONS.map(option => (
          <ReasonOptionButton
            key={option.value}
            option={option}
            selectedReason={selectedReason}
            onSelect={onSelect}
          />
        ))}
      </div>

      {selectedReason === 'other' && (
        <div className='space-y-2 pl-2'>
          <Label htmlFor='other-reason'>Please specify the reason</Label>
          <Textarea
            id='other-reason'
            placeholder='Enter reason for removing this recall...'
            value={otherReasonText}
            onChange={e => onOtherReasonChange(e.target.value)}
            rows={3}
            className='resize-none'
          />
        </div>
      )}
    </div>
  );
};

const useRemoveRecallDialog = (
  onConfirm: (reason: RemovalReason, otherReason?: string) => void,
  onOpenChange: (open: boolean) => void
) => {
  const [selectedReason, setSelectedReason] = useState<RemovalReason | ''>('');
  const [otherReasonText, setOtherReasonText] = useState('');

  const handleConfirm = () => {
    if (!selectedReason) return;
    if (selectedReason === 'other' && !otherReasonText.trim()) return;

    onConfirm(selectedReason, selectedReason === 'other' ? otherReasonText.trim() : undefined);
    setSelectedReason('');
    setOtherReasonText('');
  };

  const handleCancel = () => {
    setSelectedReason('');
    setOtherReasonText('');
    onOpenChange(false);
  };

  const canConfirm = selectedReason && (selectedReason !== 'other' || otherReasonText.trim());

  return {
    selectedReason,
    setSelectedReason,
    otherReasonText,
    setOtherReasonText,
    handleConfirm,
    handleCancel,
    canConfirm
  };
};

interface DialogContentProps {
  recallTitle?: string;
  campaignNumber?: string;
  vin?: string;
  selectedReason: RemovalReason | '';
  setSelectedReason: (reason: RemovalReason) => void;
  otherReasonText: string;
  setOtherReasonText: (text: string) => void;
  handleConfirm: () => void;
  handleCancel: () => void;
  canConfirm: boolean;
}

const DialogContentComponent: React.FC<DialogContentProps> = ({
  recallTitle,
  campaignNumber,
  vin,
  selectedReason,
  setSelectedReason,
  otherReasonText,
  setOtherReasonText,
  handleConfirm,
  handleCancel,
  canConfirm
}) => {
  return (
    <>
      <DialogHeader>
        <DialogTitle>Remove Recall</DialogTitle>
        <DialogDescription>
          Please provide a reason for removing this recall from the view.
          <DialogDescriptionContent
            recallTitle={recallTitle}
            campaignNumber={campaignNumber}
            vin={vin}
          />
        </DialogDescription>
      </DialogHeader>

      <ReasonSelection
        selectedReason={selectedReason}
        onSelect={setSelectedReason}
        otherReasonText={otherReasonText}
        onOtherReasonChange={setOtherReasonText}
      />

      <DialogFooter>
        <Button variant='outline' onClick={handleCancel}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} disabled={!canConfirm}>
          Remove Recall
        </Button>
      </DialogFooter>
    </>
  );
};

export const RemoveRecallDialog: React.FC<RemoveRecallDialogProps> = ({
  open,
  onOpenChange,
  onConfirm,
  recallTitle,
  campaignNumber,
  vin
}) => {
  const {
    selectedReason,
    setSelectedReason,
    otherReasonText,
    setOtherReasonText,
    handleConfirm,
    handleCancel,
    canConfirm
  } = useRemoveRecallDialog(onConfirm, onOpenChange);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[500px]'>
        <DialogContentComponent
          recallTitle={recallTitle}
          campaignNumber={campaignNumber}
          vin={vin}
          selectedReason={selectedReason}
          setSelectedReason={setSelectedReason}
          otherReasonText={otherReasonText}
          setOtherReasonText={setOtherReasonText}
          handleConfirm={handleConfirm}
          handleCancel={handleCancel}
          canConfirm={canConfirm}
        />
      </DialogContent>
    </Dialog>
  );
};
